import React from 'react';
import { Calendar, Flag, Users, Clock, ListChecks, History } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const statusConfig = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  todo: { label: 'A Fazer', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Em Progresso', color: 'bg-amber-100 text-amber-700' },
  review: { label: 'Em Revisão', color: 'bg-purple-100 text-purple-700' },
  done: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700' }
};

const priorityConfig = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const avatarColors = {
  blue: 'from-blue-500 to-cyan-500',
  purple: 'from-purple-500 to-pink-500',
  green: 'from-emerald-500 to-teal-500',
  orange: 'from-orange-500 to-amber-500',
  red: 'from-red-500 to-rose-500',
  indigo: 'from-indigo-500 to-blue-500'
};

export default function ListView({ tasks, members, onTaskClick, onHistoryClick }) {
  return (
    <div className="space-y-3">
      {tasks.map((task) => {
        const status = statusConfig[task.status] || statusConfig.backlog;
        const priority = priorityConfig[task.priority] || priorityConfig.medium;
        const assignedMembers = members.filter(m => task.assignees?.includes(m.id));
        const subtasksTotal = task.subtasks?.length || 0;
        const subtasksCompleted = task.subtasks?.filter(st => st.completed).length || 0;
        const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';

        return (
          <div
            key={task.id}
            className="p-4 hover:shadow-lg transition-all cursor-pointer bg-white/80 backdrop-blur-sm border-2 hover:border-blue-300 rounded-lg"
            onClick={() => onTaskClick(task)}
          >
            <div className="flex items-start gap-4">
              {/* Left: Status & Priority */}
              <div className="flex flex-col gap-2 min-w-[100px]">
                <span className={`${status.color} font-semibold text-xs px-2 py-1 rounded-md inline-flex items-center justify-center`}>
                  {status.label}
                </span>
                <span className={`${priority.color} font-semibold text-xs px-2 py-1 rounded-md inline-flex items-center justify-center`}>
                  <Flag className="w-3 h-3 mr-1" />
                  {priority.label}
                </span>
              </div>

              {/* Center: Task Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 mb-2 line-clamp-1">
                  {task.title}
                </h3>
                {task.description && (
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {task.description}
                  </p>
                )}

                {/* Tags & Indicators */}
                <div className="flex flex-wrap items-center gap-2">
                  {task.tags?.slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-xs bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-md">
                      {tag}
                    </span>
                  ))}
                  {subtasksTotal > 0 && (
                    <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded-md inline-flex items-center">
                      <ListChecks className="w-3 h-3 mr-1" />
                      {subtasksCompleted}/{subtasksTotal}
                    </span>
                  )}
                  {task.estimated_hours && (
                    <span className="text-xs bg-slate-50 text-slate-700 px-2 py-1 rounded-md inline-flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {task.estimated_hours}h
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Assignees & Date */}
              <div className="flex flex-col items-end gap-3 min-w-[150px]">
                {/* Assignees */}
                {assignedMembers.length > 0 && (
                  <div className="flex -space-x-2">
                    {assignedMembers.slice(0, 3).map((member, idx) => {
                      const colorGradient = avatarColors[member.avatar_color] || avatarColors.blue;
                      return (
                        <div 
                          key={member.id}
                          className={`w-8 h-8 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-md ring-2 ring-white`}
                          style={{ zIndex: 3 - idx }}
                          title={member.name}
                        >
                          <span className="text-xs font-bold text-white">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Due Date */}
                {task.due_date && (
                  <span className={`flex items-center text-xs font-medium px-2 py-1 rounded-lg ${
                    isOverdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(task.due_date), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                )}

                {/* History Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onHistoryClick(task);
                  }}
                  className="h-7 px-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  <History className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}